import type {
  AccountStatusViewDto,
  CreatePasswordDto,
  RequestPasswordRecoveryDto,
  SendVerificationLinkDto,
  SendVerificationLinkResult,
} from '@identity/application/dtos';
import type { GetAccountStatusOptions, ICredentialSourcePort } from '@identity/application/ports';
import type { IdentityFlow, LinkPurpose } from '@identity/domain/types';

/**
 * The two credentials a flow of this domain can be authorized with. A flow always carries one, so
 * the open endpoint of the domain has no flow and is not listed here.
 */
export const IDENTITY_AUTHORIZERS = ['link', 'session'] as const;

/**
 * Which credential a flow authorizes with: the link cookie for the public routes, the Parrot session
 * for the flows that start inside the application.
 *
 * They are never mixed and nothing compares whatever credentials happen to exist at runtime.
 */
export type IdentityAuthorizer = typeof IDENTITY_AUTHORIZERS[number];

/**
 * Which credential each flow authorizes with. The entry point declares the flow and the credential
 * is read from here, so two flows can share one credential and still be told apart in the logs.
 *
 * The compiler demands it complete: a new flow does not build until it says what authorizes it.
 */
export const FLOW_AUTHORIZERS: Record<IdentityFlow, IdentityAuthorizer> = {
  invitation: 'link',
  verifyAccount: 'session',
  recovery: 'link',
  profile: 'session',
};

/** Magic link use case contracts */
export interface LinkUseCases {
  readonly sendVerificationLink: {
    /** It resolves the uuid of the verification it just opened. Three of the four flows ignore it. */
    execute: (dto: SendVerificationLinkDto) => Promise<SendVerificationLinkResult>;
  };
}

/**
 * The link consume, which belongs to no flow: the landing serves every purpose and none of them is
 * known until the backend answers.
 */
export interface MagicLinkConsumer {
  readonly consumeMagicLink: {
    /** It resolves only with the purpose: the returned token and the account stay in the use case. */
    execute: (magicLinkToken: string) => Promise<{ purpose: LinkPurpose }>;
  };
}

/** The use cases that authorize with nothing at all, and therefore belong to no slice. */
export interface PublicUseCases {
  readonly requestPasswordRecovery: {
    execute: (dto: RequestPasswordRecoveryDto) => Promise<void>;
  };
}

/**
 * Account use case contracts. The writes resolve `void` because no auth endpoint returns a body: the
 * wizard advances by updating its cached copy of the requirement map.
 */
export interface AccountUseCases {
  readonly getAccountStatus: {
    /**
     * `signal` lets the polling cancel the request in progress when it stops, and
     * `verificationUuid` resolves the answer against one verification instead of the account.
     */
    execute: (options?: GetAccountStatusOptions) => Promise<AccountStatusViewDto>;
  };
  readonly createPassword: {
    /** `currentPassword` is what turns this write into a change; the in-session flow always sends it. */
    execute: (input: CreatePasswordDto) => Promise<void>;
  };
}

/**
 * One fully wired copy of the domain, for one flow. The same factories build every slice; only the
 * axios instance and the credential source differ, and neither is visible to a use case.
 */
export type IdentitySlice = {
  readonly linkUseCases: LinkUseCases;
  readonly accountUseCases: AccountUseCases;
  readonly credentials: ICredentialSourcePort;
};

/** Everything the composition root builds as a slice: one per flow. */
export type IdentityComposition = Record<IdentityFlow, IdentitySlice>;
